import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import FormDialog from '../../../components/ui/FormDialog'
import FormTextField from '../../../components/ui/form/FormTextField'
import { useZodForm } from '../../../lib/forms/useZodForm'
import { rejectReasonFormSchema, type RejectReasonFormValues } from './rejectReasonFormSchema'
import type { RejectReason, RejectReasonUpsert } from '../types'

interface Props {
  visible: boolean
  initial: RejectReason | null
  submitting: boolean
  nameError?: string | null
  onClearNameError: () => void
  onSubmit: (payload: RejectReasonUpsert) => void
  onHide: () => void
}

/** Ret gerekçesi ekle/düzenle formu (020: react-hook-form + zod). TR ad zorunlu; EN opsiyonel. Sıra formda yok. */
export default function RejectReasonFormDialog({
  visible,
  initial,
  submitting,
  nameError,
  onClearNameError,
  onSubmit,
  onHide,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const { control, handleSubmit, reset, setError } = useZodForm(rejectReasonFormSchema, {
    defaultValues: { nameTr: '', nameEn: '' },
  })

  useEffect(() => {
    if (visible)
      reset({
        nameTr: initial?.nameTr ?? '',
        nameEn: initial?.nameEn ?? '',
      })
  }, [visible, initial, reset])

  useEffect(() => {
    if (nameError) {
      setError('nameTr', { type: 'server', message: nameError })
      onClearNameError()
    }
  }, [nameError, setError, onClearNameError])

  const onValid = (v: RejectReasonFormValues) =>
    onSubmit({ nameTr: v.nameTr, nameEn: v.nameEn ? v.nameEn : null })

  return (
    <FormDialog
      visible={visible}
      header={initial ? t('admin.rejectReasons.form.editTitle') : t('admin.rejectReasons.form.newTitle')}
      submitLabel={t('admin.rejectReasons.form.save')}
      submitting={submitting}
      submitDisabled={submitting}
      onSubmit={handleSubmit(onValid)}
      onHide={onHide}
    >
      <div className="admin-form">
        <FormTextField control={control} name="nameTr" label={t('admin.rejectReasons.form.nameTr')} required autoFocus />
        <FormTextField control={control} name="nameEn" label={t('admin.rejectReasons.form.nameEn')} />
      </div>
    </FormDialog>
  )
}
